import {
    getMerchantKey,
    Merchant,
    MerchantBankAccountStatus,
    MerchantKycStatus,
    MerchantWorkflow,
} from '../../../../types/onboarding/Merchant';

/** Prefix of the data-testid of the row action of each workflow, e.g. kyc-merchant-{key}. */
const testIdPrefixes: Record<MerchantWorkflow, string> = {
    [MerchantWorkflow.Kyc]: 'kyc',
    [MerchantWorkflow.BankAccount]: 'bank-account',
};

/** The workflows offered on each merchant row, in display order. */
export const merchantWorkflows: MerchantWorkflow[] = [
    MerchantWorkflow.Kyc,
    MerchantWorkflow.BankAccount,
];

/**
 * What the row action of a workflow does, given how far the application has come at Bits. The application is opened only
 * while the applicant has something to do in it; once it awaits review, has been decided or has been approved, the action
 * only shows where it stands.
 */
export enum WorkflowActionState {
    /** Nothing has been done yet: the action opens the application. */
    Start = 'start',
    /** The applicant has started (or has been sent back to) the application: the action opens it again. */
    Continue = 'continue',
    /** The application awaits review at Bits or Walley: nothing more is needed from the applicant. */
    InReview = 'in-review',
    /** The application was declined, failed or was cancelled; it cannot be reopened. */
    Declined = 'declined',
    /** The application was approved. */
    Done = 'done',
}

export interface WorkflowAction {
    state: WorkflowActionState;
    /** Button text: the name of the workflow, whatever the state. The icon tells the state apart. */
    label: string;
    /** Whether the action opens the Bits application in a new tab. */
    opens: boolean;
    /** data-testid of the button, {workflow}-merchant-{key}, the same in every state (see data-workflow-state). */
    testId: string;
}

/** A row action before it is bound to a workflow and merchant. */
type WorkflowActionTemplate = Pick<WorkflowAction, 'state' | 'opens'>;

const readOnly = (state: WorkflowActionState): WorkflowActionTemplate => ({ state, opens: false });

const opening = (state: WorkflowActionState): WorkflowActionTemplate => ({ state, opens: true });

/** Button text of each workflow. */
const labels: Record<MerchantWorkflow, string> = {
    [MerchantWorkflow.Kyc]: 'KYC',
    [MerchantWorkflow.BankAccount]: 'Bank account',
};

const kycActions: Record<
    Exclude<MerchantKycStatus, MerchantKycStatus.NotAvailable>,
    WorkflowActionTemplate
> = {
    [MerchantKycStatus.NotStarted]: opening(WorkflowActionState.Start),
    [MerchantKycStatus.InProgress]: opening(WorkflowActionState.Continue),
    [MerchantKycStatus.InReview]: readOnly(WorkflowActionState.InReview),
    [MerchantKycStatus.Approved]: readOnly(WorkflowActionState.Done),
    [MerchantKycStatus.Declined]: readOnly(WorkflowActionState.Declined),
};

const bankAccountActions: Record<
    Exclude<MerchantBankAccountStatus, MerchantBankAccountStatus.NotAvailable>,
    WorkflowActionTemplate
> = {
    [MerchantBankAccountStatus.NotStarted]: opening(WorkflowActionState.Start),
    [MerchantBankAccountStatus.Pending]: opening(WorkflowActionState.Continue),
    [MerchantBankAccountStatus.Verified]: readOnly(WorkflowActionState.Done),
    [MerchantBankAccountStatus.Failed]: readOnly(WorkflowActionState.Declined),
};

const templateFor = (
    merchant: Merchant,
    workflow: MerchantWorkflow
): WorkflowActionTemplate | undefined => {
    if (workflow === MerchantWorkflow.Kyc)
        return merchant.kycStatus === MerchantKycStatus.NotAvailable
            ? undefined
            : kycActions[merchant.kycStatus];

    return merchant.bankAccountStatus === MerchantBankAccountStatus.NotAvailable
        ? undefined
        : bankAccountActions[merchant.bankAccountStatus];
};

/**
 * The row action for the workflow of the merchant, or undefined when the merchant has no such application (see
 * {@link hasWorkflow}), so no action is shown.
 */
export const workflowAction = (
    merchant: Merchant,
    workflow: MerchantWorkflow
): WorkflowAction | undefined => {
    const template = templateFor(merchant, workflow);

    if (!template) return undefined;

    return {
        ...template,
        label: labels[workflow],
        testId: `${testIdPrefixes[workflow]}-merchant-${getMerchantKey(merchant)}`,
    };
};
